import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import OfficeBudgetSettings from './office-budget-settings.js';
import InsightsControls from './controls.js';
import TotalOfficeSpendLine from './total-office-spend-line.js';
import SpendByBudgetCodePieTable from './spend-by-budget-code-pie-table.js';
import SpendToBudgetProgressPie from './spend-to-budget-progress-pie.js';
import ReportDownload from './report-downloads.js';
import {
  doGetLocationDataView,
  openDataViewRange,
  openDataViewLocations,
} from '../../actions/data-view-actions.js';
import { openFeatureTour } from '../../ducks/feature-tours.js';
import { hasSeenFeatureTour } from '../../helpers/feature-tour-helpers.js';
import './insights-budgets.css';
import { Button, Icon } from 'semantic-ui-react';

const DATA_VIEWS = [
  'customer_purchase_budget_code_dates',
  'customer_purchase_dates',
  'customer_purchase_line_item_budget_code_dates',
];

export class InsightsBudgets extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      windowWidth: window.innerWidth,
      fetchedStart: null,
      fetchedEnd: null,
      fetchedLocationIds: null,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeThrottler);
    this._refreshData({
      locations: this.props.viewingLocations,
      startDate: this.props.startDate,
      endDate: this.props.endDate,
    });
    if (this.props.displayBudgetInsightsTour) {
      this.introTimeout = setTimeout(() => {
        this.props.actions.openFeatureTour({ name: 'budget_insights_enabled' });
      }, 100);
    }
    window.totango.track('Viewed Budget Insights.', 'Reporting');
  }

  componentWillUnmount() {
    this.introTimeout && window.clearTimeout(this.introTimeout);
    this.resizeTimeout && window.clearTimeout(this.resizeTimeout);
    window.removeEventListener('resize', this.resizeThrottler);
  }

  _haveLocationsUpdated = (prevProps) => {
    return (
      this.props.viewingLocations.map((l) => l.id).join('') !==
        prevProps.viewingLocations.map((l) => l.id).join('') ||
      this.props.viewingLocations.map((l) => l.updated_at).join('') !==
        prevProps.viewingLocations.map((l) => l.updated_at).join('')
    );
  };

  _haveDatesUpdated = (prevProps) => {
    return (
      this.props.startDate.toJSON() !== prevProps.startDate.toJSON() ||
      this.props.endDate.toJSON() !== prevProps.endDate.toJSON()
    );
  };

  _locationHasAccessToBudgetCodes = () => {
    let matchingPreferences = this.props.viewingLocations.filter((loc) => {
      return loc.pref_enable_custom_budget_codes;
    });
    return matchingPreferences.length === this.props.viewingLocations.length;
  };

  resizeThrottler = () => {
    if (this.resizeTimeout) {
      return;
    }
    this.resizeTimeout = window.setTimeout(this.resizeListener, 200);
  };

  resizeListener = () => {
    this.resizeTimeout = null;
    if (this.state.windowWidth === window.innerWidth) return;
    this.setState(
      Object.assign({}, this.state, {
        windowWidth: window.innerWidth,
      })
    );
  };

  _onChangeDates = ({ startDate, endDate }) => {
    if (!startDate || !endDate) {
      return;
    }
    this.props.actions.openDataViewRange({
      startDate,
      endDate,
    });
  };

  _onChangeLocations = (locations) => {
    this.props.actions.openDataViewLocations(locations);
  };

  _refreshData = ({ locations, startDate, endDate }) => {
    let start = new Date(startDate.startOf('day')).toJSON();
    let end = new Date(endDate.endOf('day')).toJSON();
    locations.map((location) => {
      DATA_VIEWS.map((view) => {
        return this.props.actions.doGetLocationDataView({
          location_id: location.id,
          view,
          start,
          end,
        });
      });
      return location;
    });
    this.setState({
      fetchedStart: startDate.toJSON(),
      fetchedEnd: endDate.toJSON(),
      fetchedLocationIds: locations.map((l) => l.id).join(','),
    });
  };

  _applyIsDisabled = () => {
    const { fetchedStart, fetchedEnd, fetchedLocationIds } = this.state;
    if (!fetchedStart) return false;
    return (
      fetchedStart === this.props.startDate.toJSON() &&
      fetchedEnd === this.props.endDate.toJSON() &&
      fetchedLocationIds === this.props.viewingLocations.map((l) => l.id).join(',')
    );
  };

  beginInAppTutorial = () => {
    this.props.actions.openFeatureTour({ name: 'budget_insights_enabled' });
  };

  render() {
    return (
      <div className="insights-budgets-container">
        <div className="insights-what-am-i-seeing" onClick={this.beginInAppTutorial}>
          <span className="insights-what-am-i-seeing-text">What am I seeing?</span>
          <Icon name="question circle outline" />
        </div>

        <div className="insights-header-row">
          <h1 className="insights-page-title">Insights</h1>
          <InsightsControls
            startDate={this.props.startDate}
            endDate={this.props.endDate}
            openLocations={this.props.viewingLocations}
            windowWidth={this.state.windowWidth}
            onChangeDates={this._onChangeDates}
            onChangeLocations={this._onChangeLocations}
          />
          <Button
            className="insights-apply-button"
            disabled={this._applyIsDisabled()}
            onClick={() => {
              this._refreshData({
                locations: this.props.viewingLocations,
                startDate: this.props.startDate,
                endDate: this.props.endDate,
              });
            }}>
            Apply
          </Button>
        </div>
        <div className="insights-budget-main">
          <div className="insights-budget-left">
            <hr className="insights-budget-divider" />
            <div className="insights-budget-spend">
              <div className="insights-budget-spend-header">
                <div className="insights-total-spend-title-row">
                  <h2 className="insights-total-spend-title">Total spend</h2>
                  <span className="insights-disclaimer-text">
                    *Data on this page is based on the date orders are submitted
                  </span>
                </div>
                <ReportDownload
                  locations={this.props.viewingLocations}
                  startDate={this.props.startDate}
                  endDate={this.props.endDate}
                />
              </div>
              <TotalOfficeSpendLine
                startDate={this.props.startDate}
                endDate={this.props.endDate}
                locations={this.props.viewingLocations}
                windowWidth={this.state.windowWidth}
              />
            </div>
            <hr className="insights-budget-divider" />
            <div className="insights-budget-code-spend">
              <SpendByBudgetCodePieTable />
            </div>
          </div>
          <div className="insights-budget-right hide-when-printing">
            <hr className="insights-budget-divider" />
            <h2 className="insights-budget-heading">Budget</h2>
            <SpendToBudgetProgressPie />
            <OfficeBudgetSettings locations={this.props.viewingLocations} />
            {!this.props.budgetSpendPreference && (
              <React.Fragment>
                <hr className="insights-budget-divider insights-additional-spend-divider" />
                <h2 className="insights-budget-heading">
                  Notifications <Icon name="bell outline" />
                </h2>
                <p className="insights-additional-spend-bold">Budget spend alerts</p>
                <p className="insights-additional-spend-desc">
                  Be notified when your office spend reaches a percentage of your monthly budget.
                </p>
                <Button
                  className="insights-upload-invoices-button"
                  primary
                  onClick={() => { window.location.href = '/settings/notifications#user-notifications'; }}>
                  Set up notifications
                </Button>
              </React.Fragment>
            )}
            <hr className="insights-budget-divider insights-additional-spend-divider" />
            <h2 className="insights-budget-heading">
              Additional spend <Icon name="file outline" />
            </h2>
            <p className="insights-additional-spend-bold">Track all your spend in one place</p>
            <p className="insights-additional-spend-desc">
              Upload any vendor invoice here and have it automatically uploaded to your Insights for a more holistic view of your spend.
            </p>
            <Button
              as="a"
              href="/insights/vendor-invoices"
              className="insights-upload-invoices-button"
              primary>
              Upload invoices
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

InsightsBudgets.propTypes = {
  viewingLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      pref_enable_custom_budget_codes: PropTypes.bool,
    })
  ).isRequired,
  displayCodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  startDate: PropTypes.instanceOf(moment).isRequired,
  endDate: PropTypes.instanceOf(moment).isRequired,
  actions: PropTypes.shape({
    doGetLocationDataView: PropTypes.func.isRequired,
    openDataViewRange: PropTypes.func.isRequired,
    openDataViewLocations: PropTypes.func.isRequired,
    openFeatureTour: PropTypes.func.isRequired,
  }).isRequired,
  displayBudgetInsightsTour: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    displayBudgetInsightsTour: !hasSeenFeatureTour(
      'budget_insights_enabled',
      state.featureTours.items,
      state.featureTourAttempts.summary
    ),
    isRequesting:
      !state.locations.open.created_at ||
      state.locations.requesting.length > 0 ||
      state.employees.isRequesting,
    viewingLocations: state.dataViews.openLocations.length
      ? state.dataViews.openLocations
      : [state.locations.open],
    startDate: state.dataViews.openRange.startDate,
    endDate: state.dataViews.openRange.endDate,
    displayCodes: state.customerBudgetCodes.items,
    budgetSpendPreference:
      state.employees.user.pref_notify_location_spend_budget_month_threshold,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        doGetLocationDataView,
        openDataViewRange,
        openDataViewLocations,
        openFeatureTour,
      },
      dispatch
    ),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.dataViews.openRange === next.dataViews.openRange &&
    prev.dataViews.openLocations === next.dataViews.openLocations &&
    prev.locations.requesting === next.locations.requesting &&
    prev.employees.isRequesting === next.employees.isRequesting &&
    prev.featureTours.items === next.featureTours.items &&
    prev.featureTourAttempts.summary === next.featureTourAttempts.summary &&
    prev.customerBudgetCodes.items === next.customerBudgetCodes.items &&
    prev.employees.user.pref_notify_location_spend_budget_month_threshold ===
      next.employees.user.pref_notify_location_spend_budget_month_threshold
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(InsightsBudgets);
